// src/auth/RequireRole.jsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../utils/userContext";
import { canAccess } from "../utils/access";
import type { RequireRoleProps } from "../types/UserTypes";
import WorkspaceLoading from "../components/WorkspaceLoading";

export default function RequireRole({
  allowedRoles,
  children,
  redirectTo = "/timeentry",
}: RequireRoleProps) {
  const { user, tenants, activeTenantId, loading: isLoading } = useUser();
  const location = useLocation();

  if (isLoading) return <WorkspaceLoading />;

  if (!user) {
    if (location.pathname === "/") return null;
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  const roleId: number | null = user.roleId;

  // No role — redirect to tenant picker or root (RequireTenant handles no-access UI)
  if (!roleId) {
    if (tenants.length > 0 && !activeTenantId) {
      return <Navigate to="/selecttenant" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (!canAccess(roleId, allowedRoles)) {
    if (location.pathname === redirectTo) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;
  }

  return children;
}
